import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, Stack, TextField, Typography } from '@mui/material';
import { CheckCircleOutline, EditOutlined, RestartAlt, VisibilityOutlined, WarningAmberOutlined } from '@mui/icons-material';
import type { PatientV2ReviewSectionPayload } from '../../../../services/patients/patientsV2Service';
import type { PatientV2Document } from './types';
import { BrighterGreen, DarkGreen } from '../../../../app/theme/tokens/colors';

interface Props {
  documents: PatientV2Document[];
  value?: PatientV2ReviewSectionPayload;
  onChange: (value?: PatientV2ReviewSectionPayload) => void;
  onOpenDocument: (document: PatientV2Document) => Promise<void>;
}

const toDateInput = (value?: string) => String(value ?? '').match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? '';

export const PatientDocumentReviewActions = ({ documents, value, onChange, onOpenDocument }: Props) => {
  const [pendingOpen, setPendingOpen] = useState(false);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [expirationDates, setExpirationDates] = useState<Record<number, string>>({});
  const [error, setError] = useState('');
  const selectedDocuments = useMemo(() => documents.filter((document) => selected[document.id]), [documents, selected]);

  const openPending = () => {
    const current = value?.documentPendencies ?? [];
    setSelected(Object.fromEntries(current.map((item) => [Number(item.documentId), true])));
    setComments(Object.fromEntries(current.map((item) => [Number(item.documentId), item.comment ?? ''])));
    setError('');
    setPendingOpen(true);
  };

  const savePending = () => {
    if (!selectedDocuments.length) {
      setError('Selecione ao menos um documento.');
      return;
    }
    onChange({ decision: 'pending', documentPendencies: selectedDocuments.map((document) => ({ documentId: document.id, comment: String(comments[document.id] ?? '').trim() })) });
    setPendingOpen(false);
  };

  const openApproval = () => {
    const current = value?.documentApprovals ?? [];
    const currentById = new Map(current.map((item) => [Number(item.documentId), toDateInput(item.documentExpiresAt)]));
    setExpirationDates(Object.fromEntries(documents.map((document) => [document.id, currentById.get(document.id) ?? toDateInput(document.documentExpiresAt)])));
    setApprovalOpen(true);
  };

  const saveApproval = () => {
    onChange({ decision: 'approved', documentApprovals: documents.map((document) => ({ documentId: document.id, documentExpiresAt: expirationDates[document.id] || undefined })) });
    setApprovalOpen(false);
  };

  if (!documents.length) return null;

  return (
    <>
      {value?.decision === 'approved' ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Chip icon={<CheckCircleOutline />} label="Aprovado" size="small" sx={{ color: '#287a20', backgroundColor: '#eaf6e6', fontWeight: 700 }} />
          <Button size="small" onClick={() => onChange(undefined)} startIcon={<RestartAlt />} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
        </Stack>
      ) : value?.decision === 'pending' ? (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Chip icon={<WarningAmberOutlined />} label="Pendente" size="small" sx={{ color: '#9a5b00', backgroundColor: '#fff4dc', fontWeight: 700 }} />
          <Button size="small" onClick={openPending} startIcon={<EditOutlined />} sx={{ textTransform: 'none' }}>
            Alterar
          </Button>
          <Button size="small" onClick={() => onChange(undefined)} startIcon={<RestartAlt />} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
        </Stack>
      ) : (
        <Stack direction="row" spacing={0.75}>
          <Button
            size="small"
            variant="contained"
            startIcon={<WarningAmberOutlined />}
            onClick={openPending}
            sx={{ color: '#fff', backgroundColor: '#b7791f', '&:hover': { backgroundColor: '#8c5a14' }, textTransform: 'none' }}
          >
            Registrar pendências
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<CheckCircleOutline />}
            onClick={openApproval}
            sx={{ backgroundColor: BrighterGreen, '&:hover': { backgroundColor: DarkGreen }, textTransform: 'none' }}
          >
            Aprovar documentos
          </Button>
        </Stack>
      )}

      <Dialog open={pendingOpen} onClose={() => setPendingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Registrar pendências — Documentos</DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Stack spacing={1.25}>
            {documents.map((document) => {
              const checked = Boolean(selected[document.id]);
              return (
                <Box key={document.id} sx={{ p: 1.25, border: '1px solid #e3e8e4', borderRadius: 1.5 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <FormControlLabel
                      control={<Checkbox checked={checked} onChange={(_, checkedValue) => setSelected((current) => ({ ...current, [document.id]: checkedValue }))} />}
                      label={
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {document.name}
                        </Typography>
                      }
                    />
                    <IconButton size="small" onClick={() => void onOpenDocument(document)}>
                      <VisibilityOutlined fontSize="small" />
                    </IconButton>
                  </Stack>
                  {checked && (
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      size="small"
                      value={comments[document.id] ?? ''}
                      onChange={(event) => setComments((current) => ({ ...current, [document.id]: event.target.value }))}
                      placeholder="Descreva a pendência, se necessário"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePending} sx={{ backgroundColor: '#9a5b00', '&:hover': { backgroundColor: '#744500' } }}>
            Salvar pendências
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approvalOpen} onClose={() => setApprovalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Aprovar documentos</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Informe a data de validade quando aplicável. Campos vazios permanecem sem validade definida.
          </Typography>
          <Stack spacing={1.25}>
            {documents.map((document) => (
              <Box key={document.id} sx={{ p: 1.25, border: '1px solid #e3e8e4', borderRadius: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {document.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {document.type}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <TextField
                      label="Data de validade"
                      type="date"
                      size="small"
                      value={expirationDates[document.id] ?? ''}
                      onChange={(event) => setExpirationDates((current) => ({ ...current, [document.id]: event.target.value }))}
                      InputLabelProps={{ shrink: true }}
                    />
                    <IconButton size="small" onClick={() => void onOpenDocument(document)}>
                      <VisibilityOutlined fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveApproval} sx={{ backgroundColor: BrighterGreen, '&:hover': { backgroundColor: DarkGreen } }}>
            Confirmar aprovação
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
